<script lang="ts" setup>
  import { Button } from '@/components/ui/button'
  import { PlusIcon, SearchIcon } from 'lucide-vue-next'
  import AiChatSidebarItem from '@/components/app/cases/chat/AiChatSidebarItem.vue'
  import { inject, ref, type Ref, computed } from 'vue'
  import type { AIChatSession, CustomerCase } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      AiChat: 'AI Chat',
      Search: 'Search',
      NoMessage: 'No chats.',
      OnlyMessagesAppearHere: 'Only messages appear here',
      NewChat: 'New chat'
    },
    de: {
      AiChat: 'KI Chat',
      Search: 'Suchen',
      NoMessage: 'Keine Chats.',
      OnlyMessagesAppearHere: 'Hier erscheinen Ihre Chats',
      NewChat: 'Neuer Chat'
    }
  })

  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const changeActiveChatSession = inject<Function>(
    'changeActiveChatSession',
    (chatSession: AIChatSession) => {}
  )
  const createNewChatSession = inject<Function>(
    'createNewChatSession',
    async (message?: string) => {}
  )

  const sortedChatSessions = computed(() => {
    if (!caseData.value?.chat_sessions) return []
    return [...caseData.value.chat_sessions].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
  })
</script>
<template>
  <div class="w-72 flex-none">
    <div class="flex h-full">
      <!-- Chat Threads -->
      <div class="size-full truncate rounded-s-lg border-x border-gray-200 bg-white">
        <div class="flex h-full flex-col">
          <!-- Header -->
          <div class="flex items-center justify-between gap-x-2 border-b border-gray-200 px-5 py-2">
            <span
              id="hs-pro-chat-sidebar-label"
              class="block truncate font-semibold text-gray-800"
            >
              {{ t('AiChat') }}
            </span>

            <div class="flex items-center">
              <!-- Select -->
              <div class="relative inline-block">
                <div class="absolute end-2 top-1/2 -translate-y-1/2"></div>
              </div>
              <!-- End Select -->

              <div
                class="relative ms-2 flex items-center gap-x-1 ps-2 before:absolute before:start-0 before:top-1/2
                  before:h-4 before:w-px before:-translate-y-1/2 before:bg-gray-200"
              >
                <!-- Button -->
                <button
                  type="button"
                  class="flex size-8 items-center justify-center gap-x-3 rounded-full text-sm text-gray-600 hover:bg-gray-100
                    focus:bg-gray-100 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                  @click="createNewChatSession"
                >
                  <PlusIcon class="size-4 shrink-0" />
                  <span class="sr-only">{{ t('NewChat') }}</span>
                </button>
                <!-- End Button -->
              </div>
            </div>
          </div>
          <!-- End Header -->

          <!-- Search -->
          <div class="border-b border-gray-200">
            <!-- SearchBox -->
            <div class="relative">
              <div class="relative">
                <div
                  class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-5"
                >
                  <SearchIcon class="size-4 shrink-0 text-gray-400" />
                </div>
                <input
                  type="text"
                  class="rounded-0 block w-full border-0 bg-white px-12 py-1.5 placeholder:text-gray-500 focus:outline-none
                    focus:ring-0 disabled:pointer-events-none disabled:opacity-50 md:text-[13px]"
                  role="combobox"
                  aria-expanded="false"
                  :placeholder="`${t('Search')}`"
                  data-hs-combo-box-input
                />
              </div>
            </div>
            <!-- End Searchbox -->
          </div>
          <!-- End Search -->

          <div
            class="h-full overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-full
              [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-gray-100
              [&::-webkit-scrollbar]:w-2"
          >
            <!-- Tab Content -->
            <div class="h-full">
              <template v-if="caseData.chat_sessions.length > 0">
                <AiChatSidebarItem
                  v-for="session in sortedChatSessions"
                  :key="session.uuid"
                  :chat-session="session"
                  @click="changeActiveChatSession(session)"
                />
              </template>
              <!-- Empty State -->
              <div
                v-else
                class="flex h-full flex-col items-center justify-center p-5 text-center"
              >
                <svg
                  class="mx-auto mb-4 w-48"
                  width="178"
                  height="90"
                  viewBox="0 0 178 90"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="27"
                    y="50.5"
                    width="124"
                    height="39"
                    rx="7.5"
                    fill="currentColor"
                    class="fill-white"
                  />
                  <rect
                    x="27"
                    y="50.5"
                    width="124"
                    height="39"
                    rx="7.5"
                    stroke="currentColor"
                    class="stroke-gray-50"
                  />
                  <rect
                    x="34.5"
                    y="58"
                    width="24"
                    height="24"
                    rx="12"
                    fill="currentColor"
                    class="fill-gray-50"
                  />
                  <rect
                    x="66.5"
                    y="61"
                    width="60"
                    height="6"
                    rx="3"
                    fill="currentColor"
                    class="fill-gray-50"
                  />
                  <rect
                    x="66.5"
                    y="73"
                    width="77"
                    height="6"
                    rx="3"
                    fill="currentColor"
                    class="fill-gray-50"
                  />
                  <rect
                    x="19.5"
                    y="28.5"
                    width="139"
                    height="39"
                    rx="7.5"
                    fill="currentColor"
                    class="fill-white"
                  />
                  <rect
                    x="19.5"
                    y="28.5"
                    width="139"
                    height="39"
                    rx="7.5"
                    stroke="currentColor"
                    class="stroke-gray-100"
                  />
                  <rect
                    x="27"
                    y="36"
                    width="24"
                    height="24"
                    rx="12"
                    fill="currentColor"
                    class="fill-gray-100"
                  />
                  <rect
                    x="59"
                    y="39"
                    width="60"
                    height="6"
                    rx="3"
                    fill="currentColor"
                    class="fill-gray-100"
                  />
                  <rect
                    x="59"
                    y="51"
                    width="92"
                    height="6"
                    rx="3"
                    fill="currentColor"
                    class="fill-gray-100"
                  />
                  <g filter="url(#filter1)">
                    <rect
                      x="12"
                      y="6"
                      width="154"
                      height="40"
                      rx="8"
                      class="fill-white"
                      shape-rendering="crispEdges"
                    />
                    <rect
                      x="12.5"
                      y="6.5"
                      width="153"
                      height="39"
                      rx="7.5"
                      stroke="currentColor"
                      class="stroke-gray-100"
                      shape-rendering="crispEdges"
                    />
                    <rect
                      x="20"
                      y="14"
                      width="24"
                      height="24"
                      rx="12"
                      fill="currentColor"
                      class="fill-gray-200"
                    />
                    <rect
                      x="52"
                      y="17"
                      width="60"
                      height="6"
                      rx="3"
                      fill="currentColor"
                      class="fill-gray-200"
                    />
                    <rect
                      x="52"
                      y="29"
                      width="106"
                      height="6"
                      rx="3"
                      fill="currentColor"
                      class="fill-gray-200"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter1"
                      x="0"
                      y="0"
                      width="178"
                      height="64"
                      filterUnits="userSpaceOnUse"
                      color-interpolation-filters="sRGB"
                    >
                      <feFlood
                        flood-opacity="0"
                        result="BackgroundImageFix"
                      />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="6" />
                      <feGaussianBlur stdDeviation="6" />
                      <feComposite
                        in2="hardAlpha"
                        operator="out"
                      />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_1187_14810"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_1187_14810"
                        result="shape"
                      />
                    </filter>
                  </defs>
                </svg>

                <div class="mx-auto max-w-sm text-wrap">
                  <p class="mt-2 font-medium text-gray-800">{{ t('NoMessage') }}</p>
                  <p class="mb-5 text-sm text-gray-500">{{ t('OnlyMessagesAppearHere') }}</p>
                </div>
              </div>
              <!-- Empty State -->
            </div>
          </div>
        </div>
      </div>
      <!-- End Chat Threads -->
    </div>
  </div>
</template>
